import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { getAeoCopy, getFaqsForPath } from "@/config/aeo";
import type { AppLocale } from "@/i18n/constants";

type Props = {
  path: string;
  tone?: "bg" | "white";
};

/** FAQ answers render in the HTML (inside <details>) so crawlers and AI agents can read them. */
export function FaqSection({ path, tone = "white" }: Props) {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  const copy = useMemo(() => getAeoCopy(locale), [locale]);
  const faqs = useMemo(() => getFaqsForPath(locale, path), [locale, path]);

  if (faqs.length === 0) return null;

  return (
    <section id="faq" aria-labelledby="faq-heading" className={tone === "white" ? "bg-white" : "bg-brand-bg"}>
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-6 md:py-32 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-8">
        <div className="lg:sticky lg:top-[calc(var(--banner-h,0px)+7rem)] lg:h-fit">
          <h2
            id="faq-heading"
            data-reveal=""
            className="font-display text-[clamp(2rem,4vw,3.25rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-brand-primary"
          >
            {copy.title}
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-ink-soft">
            {copy.subtitle}
          </p>
        </div>

        <div className="border-t border-brand-primary/12">
          {faqs.map((item, i) => (
            <details key={i} className="group border-b border-brand-primary/12">
              <summary className="group/q flex min-h-16 cursor-pointer list-none items-center justify-between gap-6 py-5 text-left [&::-webkit-details-marker]:hidden">
                <h3 className="text-lg font-semibold leading-snug text-brand-primary transition-colors duration-300 group-hover/q:text-teal-strong">{item.question}</h3>
                <span
                  aria-hidden="true"
                  className="grid size-9 shrink-0 place-items-center rounded-full border border-brand-primary/15 text-brand-primary transition-[rotate,background-color,border-color] duration-300 group-hover/q:border-brand-secondary group-hover/q:bg-teal-soft group-open:rotate-45 group-open:border-brand-secondary group-open:bg-brand-secondary"
                >
                  <Plus className="size-4" />
                </span>
              </summary>
              <div className="space-y-3 pb-7 pr-12 text-lg leading-relaxed text-ink-soft">
                {item.answer.map((paragraph, pi) => (
                  <p key={pi}>{paragraph}</p>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
